import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Plus,
  ArrowRightLeft,
  TrendingUp,
  UserMinus,
  UserPlus,
  Save,
  Lock,
} from "lucide-react";
import FormControl from "../common/FormControl";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Skeleton from "../common/Skeleton";
import Pagination from "../common/Pagination";
import postingService, {
  Posting,
  VacantPosition,
  OccupiedPosition,
} from "../../services/postingService";
import apiClient from "../../services/apiService";
import { isPickableUser } from "../../utils/userPickable";
import "./OrganizationPositions.css";
import ApprovalDot from "../common/ApprovalDot";
import WorkflowModal from "../common/WorkflowModal";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import NoDataFound from "../common/NoDataFound";
import StatusSwitch from "../common/StatusSwitch";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { ValidatedInput, ValidatedDatePicker } from "../../validation/ValidatedFields";
import Toast, { type ToastMessage } from "../common/Toast";

const RBAC_ACTIVITY = "ORG_ORGANOGRAM";

interface UserOption {
  id: number;
  name: string;
  email: string;
}

type PostingType = "Direct" | "Promotion" | "Rotation" | "Vacancy";

const POSTING_TYPES: {
  value: PostingType;
  label: string;
  icon: React.ReactNode;
  desc: string;
  color: string;
}[] = [
    {
      value: "Direct",
      label: "Recruitment",
      icon: <UserPlus size={18} />,
      desc: "Assign user to vacant position",
      color: "#475569",
    },
    {
      value: "Promotion",
      label: "Promotion",
      icon: <TrendingUp size={18} />,
      desc: "Promote user to new position",
      color: "#166534",
    },
    {
      value: "Rotation",
      label: "Rotation",
      icon: <ArrowRightLeft size={18} />,
      desc: "Swap two position incumbents",
      color: "#1e40af",
    },
    {
      value: "Vacancy",
      label: "Relieving",
      icon: <UserMinus size={18} />,
      desc: "Relieve user from position",
      color: "#92400e",
    },
  ];

const TYPE_COLORS: Record<string, string> = {
  Direct: "status-badge xs neutral",
  Promotion: "status-badge xs success",
  Rotation: "status-badge xs info",
  Vacancy: "status-badge xs warning",
};

const PostingManagement: React.FC<{ compact?: boolean }> = () => {
  const viewPerm    = usePermission(RBAC_ACTIVITY, "view");
  const canView     = viewPerm.allowed;
  const modifyPerm  = usePermission(RBAC_ACTIVITY, "view+modify");
  const canModify   = modifyPerm.allowed;
  const permLoading = modifyPerm.loading;

  const { formatDate } = useEntitySettings();

  const [workflowOpenFor, setWorkflowOpenFor] = useState<Posting | null>(null);

  const [postings, setPostings] = useState<Posting[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, typeFilter, actionFilter, pageSize, statusFilter]);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Dropdown data
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [vacantPositions, setVacantPositions] = useState<VacantPosition[]>([]);
  const [occupiedPositions, setOccupiedPositions] = useState<
    OccupiedPosition[]
  >([]);
  const [allPositions, setAllPositions] = useState<VacantPosition[]>([]);

  // Workflow form
  const [selectedType, setSelectedType] = useState<PostingType>("Direct");
  const fieldVh = useFieldValidation();

  // Direct
  const [directPosId, setDirectPosId] = useState(0);
  const [directUserId, setDirectUserId] = useState(0);
  const [directDate, setDirectDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [directRef, setDirectRef] = useState("");

  // Rotation
  const [rotPosAId, setRotPosAId] = useState(0);
  const [rotPosBId, setRotPosBId] = useState(0);
  const [rotDate, setRotDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [rotRef, setRotRef] = useState("");

  // Promotion
  const [promoUserId, setPromoUserId] = useState(0);
  const [promoUserPostings, setPromoUserPostings] = useState<Posting[]>([]);
  const [promoFromPosId, setPromoFromPosId] = useState(0);
  const [promoToPosId, setPromoToPosId] = useState(0);
  const [promoDate, setPromoDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [promoRef, setPromoRef] = useState("");
  const [promoRelieveTarget, setPromoRelieveTarget] = useState(true);

  // Vacancy
  const [vacPosId, setVacPosId] = useState(0);
  const [vacDate, setVacDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [vacRef, setVacRef] = useState("");

  const fetchPostings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await postingService.getAll({
        type: typeFilter || undefined,
        action: actionFilter || undefined,
        search: searchTerm || undefined,
        is_active: statusFilter === 'active' ? 1 : 0,
        page: currentPage,
        limit: pageSize,
      });
      setPostings(res.data);
      setTotalItems(res.meta.total);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || "Failed to load postings" });
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, actionFilter, searchTerm, pageSize, statusFilter]);

  useEffect(() => {
    fetchPostings();
  }, [fetchPostings]);

  const loadDropdowns = async () => {
    setDropdownLoading(true);
    try {
      const [vac, occ, usr, allPos] = await Promise.all([
        postingService.getVacantPositions(),
        postingService.getOccupiedPositions(),
        apiClient.get<any>("/api/v1/usr/users?limit=500"),
        apiClient.get<any>("/api/v1/org/positions?page=1&limit=500"),
      ]);
      setVacantPositions(vac);
      setOccupiedPositions(occ);
      const ud = usr.data?.data || usr.data || [];
      setUserOptions(
        (Array.isArray(ud) ? ud : []).filter(isPickableUser).map((u: any) => ({
          id: u.id,
          name:
            [u.first_name, u.last_name].filter(Boolean).join(" ") ||
            u.login_id ||
            `User #${u.id}`,
          email: u.email || "",
        })),
      );
      const pd = allPos.data?.data || allPos.data || [];
      setAllPositions(
        (Array.isArray(pd) ? pd : []).map((p: any) => ({
          id: p.id,
          name: p.name,
          code: p.code || "",
          authority_name: p.authority_name,
          location_name: p.location_name,
          is_approved: p.is_approved,
        })),
      );
    } catch (e) {
      console.error("Failed to load dropdown data:", e);
    } finally {
      setDropdownLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedType("Direct");
    resetForms();
    setModalError(null);
    fieldVh.reset();
    setIsModalOpen(true);
    loadDropdowns();
  };

  const resetForms = () => {
    const today = new Date().toISOString().split("T")[0];
    setDirectPosId(0);
    setDirectUserId(0);
    setDirectDate(today);
    setDirectRef("");
    setRotPosAId(0);
    setRotPosBId(0);
    setRotDate(today);
    setRotRef("");
    setPromoUserId(0);
    setPromoUserPostings([]);
    setPromoFromPosId(0);
    setPromoToPosId(0);
    setPromoDate(today);
    setPromoRef("");
    setPromoRelieveTarget(true);
    setVacPosId(0);
    setVacDate(today);
    setVacRef("");
  };

  // Load user's active postings for promotion
  useEffect(() => {
    if (selectedType === "Promotion" && promoUserId > 0) {
      postingService
        .getByUser(promoUserId)
        .then((posts) => {
          const activeJoins = posts.filter(
            (p) => p.action === "JOIN" && !p.end_date,
          );
          setPromoUserPostings(activeJoins);
          if (activeJoins.length === 1)
            setPromoFromPosId(activeJoins[0].position_id);
        })
        .catch(() => setPromoUserPostings([]));
    }
  }, [promoUserId, selectedType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setModalError(null);

    try {
      switch (selectedType) {
        case "Direct":
          if (!directPosId || !directUserId) {
            setModalError("Select position and user.");
            setSaving(false);
            return;
          }
          if (!fieldVh.validateAll([
            { name: 'direct_date', code: 305, value: directDate, required: true },
            { name: 'direct_ref', code: 110, value: directRef },
          ])) { setSaving(false); return; }
          await postingService.direct({
            position_id: directPosId,
            user_id: directUserId,
            start_date: directDate,
            reference_text: directRef || undefined,
          });
          break;

        case "Rotation":
          if (!rotPosAId || !rotPosBId) {
            setModalError("Select both positions.");
            setSaving(false);
            return;
          }
          if (!fieldVh.validateAll([
            { name: 'rot_date', code: 305, value: rotDate, required: true },
            { name: 'rot_ref', code: 110, value: rotRef },
          ])) { setSaving(false); return; }
          await postingService.rotate({
            position_a_id: rotPosAId,
            position_b_id: rotPosBId,
            effective_date: rotDate,
            reference_text: rotRef || undefined,
          });
          break;

        case "Promotion":
          if (!promoUserId || !promoFromPosId || !promoToPosId) {
            setModalError("Select user, source and target positions.");
            setSaving(false);
            return;
          }
          if (!fieldVh.validateAll([
            { name: 'promo_date', code: 305, value: promoDate, required: true },
            { name: 'promo_ref', code: 110, value: promoRef },
          ])) { setSaving(false); return; }
          await postingService.promote({
            user_id: promoUserId,
            from_position_id: promoFromPosId,
            to_position_id: promoToPosId,
            effective_date: promoDate,
            reference_text: promoRef || undefined,
            relieve_target_incumbent: promoRelieveTarget,
          });
          break;

        case "Vacancy":
          if (!vacPosId) {
            setModalError("Select a position.");
            setSaving(false);
            return;
          }
          const occ = occupiedPositions.find((p) => p.id === vacPosId);
          if (!occ) {
            setModalError("Position has no incumbent.");
            setSaving(false);
            return;
          }
          if (!fieldVh.validateAll([
            { name: 'vac_date', code: 305, value: vacDate, required: true },
            { name: 'vac_ref', code: 110, value: vacRef },
          ])) { setSaving(false); return; }
          await postingService.vacancy({
            position_id: vacPosId,
            user_id: occ.user_id,
            start_date: vacDate,
            reference_text: vacRef || undefined,
          });
          break;
      }
      setIsModalOpen(false);
      fetchPostings();
    } catch (err: any) {
      const detail =
        err.response?.data?.detail || err.message || "Operation failed";
      setModalError(
        typeof detail === "string" ? detail : JSON.stringify(detail),
      );
    } finally {
      setSaving(false);
    }
  };

  const targetOccupant = occupiedPositions.find((p) => p.id === promoToPosId);

  // --- Render type-specific forms ---

  const renderDirectForm = () => (
    <>
      <FormControl.SearchSelect
        label="Vacant Position"
        required
        value={directPosId || null}
        onChange={(value) => setDirectPosId(value ? Number(value) : 0)}
        placeholder="Select vacant position..."
        clearable
        options={vacantPositions.map((p) => ({
          value: p.id,
          label: `${p.name}${p.authority_name ? ` — ${p.authority_name}` : ""}`,
        }))}
        data-testid="SEL_posting_direct_position"
      />
      {vacantPositions.length === 0 && !dropdownLoading && (
        <span
          style={{
            fontSize: "0.75rem",
            color: "#64748b",
            marginTop: 2,
            display: "block",
          }}
        >
          No vacant positions available.
        </span>
      )}
      <FormControl.SearchSelect
        label="Assign User"
        required
        value={directUserId || null}
        onChange={(value) => setDirectUserId(value ? Number(value) : 0)}
        placeholder="Select user..."
        clearable
        options={userOptions.map((u) => ({
          value: u.id,
          label: `${u.name}${u.email ? ` (${u.email})` : ""}`,
        }))}
        data-testid="SEL_posting_direct_user"
      />
      <ValidatedDatePicker
        vh={fieldVh}
        name="direct_date"
        code={305}
        required
        label="Effective Date"
        value={directDate}
        onChange={(e: any) => setDirectDate(e.target.value)}
        data-testid="INP_posting_direct_date"
      />
      <ValidatedInput
        vh={fieldVh}
        name="direct_ref"
        code={110}
        label="Reference / Order No."
        type="text"
        value={directRef}
        onChange={(e) => setDirectRef(e.target.value)}
        placeholder="e.g. HR/2026/001"
        data-testid="INP_posting_direct_ref"
      />
    </>
  );

  const renderRotationForm = () => {
    const posA = occupiedPositions.find((p) => p.id === rotPosAId);
    const posB = occupiedPositions.find((p) => p.id === rotPosBId);
    return (
      <>
        <FormControl.SearchSelect
          label="Position A"
          required
          value={rotPosAId || null}
          onChange={(value) => setRotPosAId(value ? Number(value) : 0)}
          placeholder="Select first position..."
          clearable
          options={occupiedPositions
            .filter((p) => p.id !== rotPosBId)
            .map((p) => ({
              value: p.id,
              label: `${p.name} — ${p.occupant_name}`,
            }))}
          data-testid="SEL_posting_rotation_pos_a"
        />
        <FormControl.SearchSelect
          label="Position B"
          required
          value={rotPosBId || null}
          onChange={(value) => setRotPosBId(value ? Number(value) : 0)}
          placeholder="Select second position..."
          clearable
          options={occupiedPositions
            .filter((p) => p.id !== rotPosAId)
            .map((p) => ({
              value: p.id,
              label: `${p.name} — ${p.occupant_name}`,
            }))}
          data-testid="SEL_posting_rotation_pos_b"
        />
        {posA && posB && (
          <div
            style={{
              margin: "0.5rem 0",
              padding: "0.75rem",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              fontSize: "0.8125rem",
            }}
          >
            <strong style={{ color: "#1e40af" }}>
              Swap Preview (4 transactions):
            </strong>
            <div style={{ marginTop: 4 }}>
              <span style={{ color: "#991b1b" }}>LEAVE</span>{" "}
              {posA.occupant_name} from {posA.name}
            </div>
            <div>
              <span style={{ color: "#991b1b" }}>LEAVE</span>{" "}
              {posB.occupant_name} from {posB.name}
            </div>
            <div>
              <span style={{ color: "#166534" }}>JOIN</span>{" "}
              {posB.occupant_name} at {posA.name}
            </div>
            <div>
              <span style={{ color: "#166534" }}>JOIN</span>{" "}
              {posA.occupant_name} at {posB.name}
            </div>
          </div>
        )}
        <ValidatedDatePicker
          vh={fieldVh}
          name="rot_date"
          code={305}
          required
          label="Effective Date"
          value={rotDate}
          onChange={(e: any) => setRotDate(e.target.value)}
          data-testid="INP_posting_rotation_date"
        />
        <ValidatedInput
          vh={fieldVh}
          name="rot_ref"
          code={110}
          label="Reference / Order No."
          type="text"
          value={rotRef}
          onChange={(e) => setRotRef(e.target.value)}
          placeholder="e.g. HR/2026/ROT-001"
          data-testid="INP_posting_rotation_ref"
        />
      </>
    );
  };

  const renderPromotionForm = () => {
    const usersWithPostings = userOptions.filter((u) =>
      occupiedPositions.some((p) => p.user_id === u.id),
    );
    return (
      <>
        <FormControl.SearchSelect
          label="User to Promote"
          required
          value={promoUserId || null}
          onChange={(value) => {
            setPromoUserId(value ? Number(value) : 0);
            setPromoFromPosId(0);
            setPromoToPosId(0);
          }}
          placeholder="Select user..."
          clearable
          options={usersWithPostings.map((u) => ({
            value: u.id,
            label: `${u.name}${u.email ? ` (${u.email})` : ""}`,
          }))}
          data-testid="SEL_posting_promo_user"
        />
        {promoUserId > 0 && (
          <FormControl.SearchSelect
            label="Current Position (vacating)"
            required
            value={promoFromPosId || null}
            onChange={(value) => setPromoFromPosId(value ? Number(value) : 0)}
            placeholder="Select current position..."
            clearable
            options={promoUserPostings.map((p) => ({
              value: p.position_id,
              label: `${p.position_name} (${p.position_code})`,
            }))}
            data-testid="SEL_posting_promo_from_pos"
          />
        )}
        {promoFromPosId > 0 && (
          <FormControl.SearchSelect
            label="Promote To"
            required
            value={promoToPosId || null}
            onChange={(value) => setPromoToPosId(value ? Number(value) : 0)}
            placeholder="Select target position..."
            clearable
            options={allPositions
              // Only approved positions are valid promotion targets — positions
              // are a legacy master (is_approved=1), raw list returns all states.
              .filter((p) => p.id !== promoFromPosId && (p.is_approved ?? 0) === 1)
              .map((p) => {
                const o = occupiedPositions.find((op) => op.id === p.id);
                return {
                  value: p.id,
                  label: `${p.name}${o ? ` [${o.occupant_name}]` : " [Vacant]"}`,
                };
              })}
            data-testid="SEL_posting_promo_to_pos"
          />
        )}
        {targetOccupant && (
          <div
            style={{
              margin: "0.5rem 0",
              padding: "0.75rem",
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 8,
              fontSize: "0.8125rem",
            }}
          >
            <strong style={{ color: "#92400e" }}>Warning:</strong>{" "}
            {targetOccupant.occupant_name} currently holds this position.
            <div style={{ marginTop: 6 }}>
              <FormControl.Checkbox
                label={`Relieve ${targetOccupant.occupant_name} from this position`}
                checked={promoRelieveTarget}
                onChange={(checked) => setPromoRelieveTarget(checked)}
                data-testid="CHK_posting_promo_relieve"
              />
            </div>
          </div>
        )}
        {promoFromPosId > 0 && promoToPosId > 0 && (
          <div
            style={{
              margin: "0.5rem 0",
              padding: "0.75rem",
              background: "#dcfce7",
              border: "1px solid #bbf7d0",
              borderRadius: 8,
              fontSize: "0.8125rem",
            }}
          >
            <strong style={{ color: "#166534" }}>Transactions:</strong>
            <div style={{ marginTop: 4 }}>
              <span style={{ color: "#991b1b" }}>LEAVE</span>{" "}
              {userOptions.find((u) => u.id === promoUserId)?.name} from{" "}
              {
                promoUserPostings.find((p) => p.position_id === promoFromPosId)
                  ?.position_name
              }
            </div>
            {targetOccupant && promoRelieveTarget && (
              <div>
                <span style={{ color: "#991b1b" }}>LEAVE</span>{" "}
                {targetOccupant.occupant_name} from{" "}
                {allPositions.find((p) => p.id === promoToPosId)?.name}
              </div>
            )}
            <div>
              <span style={{ color: "#166534" }}>JOIN</span>{" "}
              {userOptions.find((u) => u.id === promoUserId)?.name} at{" "}
              {allPositions.find((p) => p.id === promoToPosId)?.name}
            </div>
          </div>
        )}
        <ValidatedDatePicker
          vh={fieldVh}
          name="promo_date"
          code={305}
          required
          label="Effective Date"
          value={promoDate}
          onChange={(e: any) => setPromoDate(e.target.value)}
          data-testid="INP_posting_promo_date"
        />
        <ValidatedInput
          vh={fieldVh}
          name="promo_ref"
          code={110}
          label="Reference / Order No."
          type="text"
          value={promoRef}
          onChange={(e) => setPromoRef(e.target.value)}
          placeholder="e.g. HR/2026/PROMO-001"
          data-testid="INP_posting_promo_ref"
        />
      </>
    );
  };

  const renderVacancyForm = () => {
    const selOcc = occupiedPositions.find((p) => p.id === vacPosId);
    return (
      <>
        <FormControl.SearchSelect
          label="Position to Vacate"
          required
          value={vacPosId || null}
          onChange={(value) => setVacPosId(value ? Number(value) : 0)}
          placeholder="Select occupied position..."
          clearable
          options={occupiedPositions.map((p) => ({
            value: p.id,
            label: `${p.name} — ${p.occupant_name}`,
          }))}
          data-testid="SEL_posting_vacancy_position"
        />
        {selOcc && (
          <div
            style={{
              margin: "0.5rem 0",
              padding: "0.75rem",
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              fontSize: "0.8125rem",
            }}
          >
            <strong style={{ color: "#991b1b" }}>LEAVE</strong> —{" "}
            {selOcc.occupant_name} will be relieved from {selOcc.name}.
          </div>
        )}
        <ValidatedDatePicker
          vh={fieldVh}
          name="vac_date"
          code={305}
          required
          label="Effective Date"
          value={vacDate}
          onChange={(e: any) => setVacDate(e.target.value)}
          data-testid="INP_posting_vacancy_date"
        />
        <ValidatedInput
          vh={fieldVh}
          name="vac_ref"
          code={110}
          label="Reference / Order No."
          type="text"
          value={vacRef}
          onChange={(e) => setVacRef(e.target.value)}
          placeholder="e.g. HR/2026/REL-001"
          data-testid="INP_posting_vacancy_ref"
        />
      </>
    );
  };

  if (!viewPerm.loading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have permission to view HR Postings." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        {/*
          HIDDEN: Active/Inactive Status Filter Toggle
          - Allows users to filter postings by status (Active = 1, Inactive = 0)
          - Default view shows Active postings only
          - Toggle to switch between Active and Inactive records
          - Updates statusFilter state which triggers fetchPostings()

        <StatusSwitch
          value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
          noun="postings"
          testId="SEL_posting_status_filter"
        />
        */}

        {/* RIGHT: Type Filter · Action Filter · Search · Create Button */}
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={typeFilter}
            onChange={v => setTypeFilter(String(v ?? ''))}
            className="filter-status"
            data-testid="SEL_posting_type_filter"
            noSearchIcon
            options={[
              { value: '', label: 'All Types' },
              { value: 'Direct', label: 'Recruitment' },
              { value: 'Promotion', label: 'Promotion' },
              { value: 'Rotation', label: 'Rotation' },
              { value: 'Vacancy', label: 'Relieving' },
            ]}
          />
          <FormControl.SearchSelect
            value={actionFilter}
            onChange={v => setActionFilter(String(v ?? ''))}
            className="filter-status"
            data-testid="SEL_posting_action_filter"
            noSearchIcon
            options={[
              { value: '', label: 'All Actions' },
              { value: 'JOIN', label: 'JOIN only' },
              { value: 'LEAVE', label: 'LEAVE only' },
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search position or user…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_posting_search"
          />
          {canModify && (
            <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_posting_create">
              Create
            </Button>
          )}
        </div>
      </div>

      <div className="netbody-content">

        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Date</th>
                    <th className="table-list-th">Position</th>
                    <th className="table-list-th">User</th>
                    <th className="table-list-th">Reference</th>
                    <th className="table-list-th">Action</th>
                    <th className="table-list-th">Type</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={[{ width: 'icon' }, '3xs', 'sm', 'xs', 'sm', 'xs', 'pill', 'pill', 'pill']} />}
                  {!loading && postings.map((p, idx) => {
                    const typ = TYPE_COLORS[p.posting_type || "Direct"] || TYPE_COLORS.Direct;
                    return (
                      <tr key={p.id} className="table-list-row">
                        <td className="table-list-cell table-list-cell--dot" title="Workflow — view timeline & act" data-testid={`BTN_posting_row_${p.id}_workflow`} onClick={e => { e.stopPropagation(); setWorkflowOpenFor(p); }}>
                          <ApprovalDot state={(p as any).workflow_state || 'draft'} onClick={() => setWorkflowOpenFor(p)} title="Workflow — view timeline & act" />
                        </td>
                        <td className="table-list-cell table-list-cell--sno">{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td className="table-list-cell"><span className="cell-code">{(p as any).code || "—"}</span></td>
                        <td className="table-list-cell">{formatDate(p.start_date)}</td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title cell-name-lock">
                            {(p as any).workflow_state === 'approved' && (
                              <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                            )}
                            {p.position_name || "-"}
                          </span>
                          {/* {p.position_code && (
                            <span className="table-list-cell-sub">({p.position_code})</span>
                          )} */}
                        </td>
                        <td className="table-list-cell"><span className="table-list-cell-title">{p.user_name || "-"}</span></td>
                        <td className="table-list-cell">{p.reference_text || "-"}</td>
                        <td className="table-list-cell">
                          {p.action || "JOIN"}
                        </td>
                        <td className="table-list-cell">
                          <span className={typ}>
                            {p.posting_type || "Direct"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && postings.length === 0 && (
                    <tr>
                      <td colSpan={9} className="table-list-cell">
                        <NoDataFound
                          title="No transactions found"
                          message="Record an HR action to begin the posting chronicle."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={currentPage}
              pageSize={pageSize}
              totalRecords={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              recordLabel="transactions"
            />
          </div>
        </div>
      </div>

      {/* New Transaction Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="md"
        data-testid="MOD_posting_form"
      >
        <Modal.Header>Create Posting</Modal.Header>
        <Modal.Body>
          <form
            id="posting-form"
            onSubmit={handleSubmit}
            className="flex flex-col gap-y-4"
          >
            {modalError && <div className="form-error">{modalError}</div>}
            {dropdownLoading && (
              <div
                style={{
                  textAlign: "center",
                  padding: "1rem",
                  color: "#64748b",
                  fontSize: "0.875rem",
                }}
              >
                Loading data...
              </div>
            )}
            {!dropdownLoading && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    Transaction Type <span className="required">*</span>
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    {POSTING_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={`BTN_posting_type_${t.value}`}
                        onClick={() => {
                          setSelectedType(t.value);
                          setModalError(null);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "0.625rem 0.75rem",
                          border: `2px solid ${selectedType === t.value ? t.color : "#e2e8f0"}`,
                          borderRadius: 8,
                          background:
                            selectedType === t.value ? `${t.color}08` : "#fff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s",
                        }}
                      >
                        <span
                          style={{
                            color:
                              selectedType === t.value ? t.color : "#94a3b8",
                          }}
                        >
                          {t.icon}
                        </span>
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              color:
                                selectedType === t.value ? t.color : "#334155",
                            }}
                          >
                            {t.label}
                          </div>
                          <div
                            style={{
                              fontSize: "0.6875rem",
                              color: "#94a3b8",
                              marginTop: 1,
                            }}
                          >
                            {t.desc}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedType === "Direct" && renderDirectForm()}
                {selectedType === "Rotation" && renderRotationForm()}
                {selectedType === "Promotion" && renderPromotionForm()}
                {selectedType === "Vacancy" && renderVacancyForm()}
              </>
            )}
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            form="posting-form"
            variant="fill"
            icon={Save}
            disabled={permLoading || !canModify || saving || dropdownLoading}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_posting_submit"
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={() => setWorkflowOpenFor(null)}
        kind="org_posting"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={fetchPostings}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default PostingManagement;
